import React from 'react'
import { Fa } from '@eltoro-ui/components'
import { DashboardMetric } from '../types'

interface ReportHubMetricCardProps {
  metric: DashboardMetric
}

export const ReportHubMetricCard: React.FC<ReportHubMetricCardProps> = ({
  metric,
}) => {
  const { title, current, total, icon, format } = metric

  // Generate accessible descriptions
  const getAriaLabel = () => {
    let label = `${title}: ${current}`
    if (total) {
      label += ` out of ${total}`
    }
    if (format === 'percentage') {
      label += ' percent'
    } else if (format === 'currency') {
      label += ' dollars'
    }
    return label
  }

  const getValueText = () => {
    if (format === 'percentage') {
      return `${current}% ${total ? `out of ${total}%` : ''}`
    } else if (format === 'currency') {
      return `$${current} ${total ? `out of $${total}` : ''}`
    }
    return `${current} ${total ? `out of ${total}` : ''}`
  }

  return (
    <article
      className="ReportHubMetricCard bg-base border-tint-gray-300 rounded-lg border p-6"
      role="img"
      aria-label={getAriaLabel()}
      tabIndex={0}
    >
      {/* Header with title and icon */}
      <div className="mb-4 flex items-start justify-between">
        <h3
          id={`metric-${title.toLowerCase().replace(/\s+/g, '-')}`}
          className="text-ml font-[500] text-tint-gray-600"
        >
          {title}
        </h3>
        {icon && (
          <div
            className="text-primary-500"
            aria-hidden="true"
            role="presentation"
          >
            <Fa icon={icon} size={1} />
          </div>
        )}
      </div>

      {/* Main metric display */}
      <div className="mb-2">
        <div className="flex items-baseline gap-2">
          <span
            className="text-3xl font-bold text-tint-gray-900"
            aria-label={getValueText()}
          >
            {current}
          </span>
          {total && (
            <span className="text-lg text-tint-gray-400" aria-hidden="true">
              / {total}
            </span>
          )}
        </div>
      </div>

      {/* Subtitle/additional info */}
      <div
        className="text-sm text-tint-gray-500"
        aria-describedby={`metric-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {metric.subTitle}
      </div>
    </article>
  )
}
